/* A product row: arrows and mouse dragging move the native horizontal scroll with the navigation's spring, and filter links swap the visible row in place. Touch and trackpads keep native scrolling. */
/* In a block, so a page with two product rows (each section includes this file) runs it twice without clashing names. */
{
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  /* Underdamped spring, about 7% overshoot a third of the way in, settled by the end (the feel of --nav-spring). */
  const spring = (t) => {
    const damping = 8.35 * t;
    return 1 - Math.exp(-damping) * (Math.cos(10.13 * t) + 0.824 * Math.sin(10.13 * t));
  };
  class C4ProductRow extends HTMLElement {
    connectedCallback() {
      this.tracks = [...this.querySelectorAll('[data-row-track]')];
      this.filters = [...this.querySelectorAll('[data-row-filter]')];
      /* Without the main collection leading, the first filter is chosen from the start: its colours and its link. */
      const lead = this.filters.find((link) => link.hasAttribute('aria-current'));
      if (lead && !lead.hasAttribute('data-row-main')) this.mark(lead);
      this.buttons = [...this.querySelectorAll('[data-row-step]')];
      this.track = this.tracks.find((track) => !track.hidden);
      if (!this.track || !this.buttons.length) return;
      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.step(Number(button.dataset.rowStep)));
      });
      this.tracks.forEach((track) => {
        track.addEventListener('scroll', () => this.update(), { passive: true });
        track.addEventListener('wheel', () => this.stop(), { passive: true });
        track.addEventListener('touchstart', () => this.stop(), { passive: true });
        track.addEventListener('pointerdown', (event) => this.grab(event));
        track.addEventListener('dragstart', (event) => event.preventDefault());
        /* A drag must not also follow the link under the pointer. */
        track.addEventListener(
          'click',
          (event) => {
            if (!this.dragged) return;
            event.preventDefault();
            event.stopPropagation();
            this.dragged = false;
          },
          true,
        );
      });
      this.filters.forEach((link, index) =>
        link.addEventListener('click', (event) => {
          event.preventDefault();
          /* Held in place by the scroll (scrollThrough): go to the filter's stretch of the page instead. */
          if (this.pinned) this.goTo(index);
          else this.show(index);
        }),
      );
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.filters.indexOf(event.target);
        if (index >= 0) this.show(index);
      });
      addEventListener('resize', () => this.update(), { passive: true });
      /* Chips that follow the page (theme setting) take the bar's glass once the heading has scrolled off beneath them. */
      const heading = this.querySelector('h2');
      const tools = this.querySelector('.row-tools');
      if (heading && tools) {
        /* Measured against the bar's line, not the chips, so it holds however the page arrived here (a jump, a restored scroll). */
        const lift = () =>
          tools.toggleAttribute('data-stuck', heading.getBoundingClientRect().bottom < 60);
        addEventListener('scroll', lift, { passive: true });
        lift();
      }
      this.update();
      if (this.dataset.scrollThrough && this.filters.length > 1) this.scrollThrough();
    }
    /* Scroll through the filters (section setting): on wide screens the row holds in place, centred under the navigation
       bar, while the page scrolls, and the scroll steps through the filters. Each filter has its stretch of the page: a
       pause as it arrives, then (with "products") its row runs sideways at the page's own speed, one pixel for one, so
       a long range takes longer and a short one passes quickly, then a pause at its last product. Stopping part way
       through a run, the page settles so a whole product rests at the row's edge. Clicking a filter goes to its stretch;
       the arrows go the way the scroll does. Off where the row doesn't fit on screen, on narrow screens and with
       reduced motion: the row is as usual. */
    scrollThrough() {
      const section = this.closest('section') || this.parentElement;
      const wide = matchMedia('(min-width: 1000px)');
      const pause = Number(this.dataset.scrollDistance) || 0.6;
      const sideways = this.dataset.scrollThrough === 'products';
      const count = this.filters.length;
      /* Held centred in the space under the navigation bar; set on each layout. */
      let top = 80;
      /* Each filter's stretch: where it starts, its pause, how far its row runs (px), and its length. */
      let stretches = [];
      const pads = () => {
        const style = getComputedStyle(section);
        return [parseFloat(style.paddingTop) || 0, parseFloat(style.paddingBottom) || 0];
      };
      const measure = () => {
        const hold = (pause * innerHeight) / 2;
        let at = 0;
        stretches = this.tracks.map((track) => {
          /* A hidden track has no width: show it for a moment to measure how far it runs. */
          const hidden = track.hidden;
          track.hidden = false;
          const run = sideways ? Math.max(0, track.scrollWidth - track.clientWidth) : 0;
          track.hidden = hidden;
          const stretch = { start: at, hold, run, length: hold * 2 + run };
          at += stretch.length;
          return stretch;
        });
        return at;
      };
      const layout = () => {
        section.style.minHeight = '';
        /* The heading's height, for the buttons beside it to centre on (product-row.css). */
        const heading = this.querySelector('.row-title');
        if (heading) this.style.setProperty('--row-title-h', `${heading.offsetHeight}px`);
        section.style.removeProperty('--held-card');
        section.style.removeProperty('--held-gap');
        const able = wide.matches && !reduceMotion.matches;
        section.toggleAttribute('data-row-held', able);
        /* The space under the navigation bar once the announcement bar has scrolled away, as it has by here. */
        const bar = document.querySelector('c4-navigation .nav-bar')?.getBoundingClientRect();
        const announce =
          parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--announce')) ||
          0;
        const below = bar?.height ? bar.bottom - announce + 8 : 72;
        /* Beside a page frame's floating logo (page-frame.css), the row is held with its title level with the logo's
           top, the same place on every screen, rather than in the middle of the room. */
        const floating = document.querySelector(
          "c4-navigation[data-logo-frame='hanging'][data-logo-scroll='float']",
        );
        const root = getComputedStyle(document.documentElement);
        const start = floating
          ? Math.max(
              below,
              parseFloat(root.getPropertyValue('--nav-top')) +
                parseFloat(root.getPropertyValue('--nav-control')) +
                (parseFloat(root.getPropertyValue('--float-gap')) || 16),
            )
          : below;
        const room = innerHeight - start - 16;
        /* Too tall to hold whole: narrower cards (4:5 tiles, so each pixel off the width takes 1.25 off the height). */
        const over = this.offsetHeight - room;
        const card = this.track.firstElementChild?.getBoundingClientRect().width;
        if (able && over > 0 && card)
          section.style.setProperty('--held-card', `${Math.max(150, card - over / 1.25)}px`);
        this.pinned = able && this.offsetHeight <= room;
        section.toggleAttribute('data-row-held', this.pinned);
        if (!this.pinned) section.style.removeProperty('--held-card');
        top = floating
          ? start
          : Math.max(below, below + (innerHeight - below - this.offsetHeight) / 2);
        /* Held level with the logo, the room left over goes half between the heading and the row (up to 140px), so
           the filters and cards sit centred in the space below the heading rather than leaving it all under them. */
        if (this.pinned && floating)
          section.style.setProperty(
            '--held-gap',
            `${Math.round(Math.min(140, Math.max(0, (room - this.offsetHeight) / 2)))}px`,
          );
        section.style.setProperty('--pin-top', `${top}px`);
        /* Held with products, the scroll moves the row: its card-by-card snapping would fight the glide, so it is off. */
        this.tracks.forEach(
          (track) => (track.style.scrollSnapType = this.pinned && sideways ? 'none' : ''),
        );
        if (this.pinned) {
          const total = measure();
          const [before, after] = pads();
          section.style.minHeight = `${before + after + this.offsetHeight + total}px`;
        }
        laidOut = this.offsetHeight;
        follow();
      };
      /* Measured again whenever the row's size changes after this (text or pictures settling, zoom, a filter whose
         note runs to another line), so the hold never keeps a stale size; its own changes don't set it off again. */
      let laidOut = 0;
      /* Its children: the row itself reports no box of its own to an observer. */
      const resized = new ResizeObserver(() =>
        requestAnimationFrame(() => {
          if (Math.abs(this.offsetHeight - laidOut) > 2) layout();
        }),
      );
      [...this.children].forEach((child) => resized.observe(child));
      let goal = 0;
      let frame = 0;
      /* How the next filter arrives: a plain crossfade when the scroll brings it, the rise when it was chosen. */
      let arrival = 'scroll';
      const glide = () => {
        const track = this.track;
        const gap = goal - track.scrollLeft;
        if (Math.abs(gap) < 1) {
          track.scrollLeft = goal;
          frame = 0;
          this.update();
          return;
        }
        track.scrollLeft += gap * 0.25;
        frame = requestAnimationFrame(glide);
      };
      const start = () => section.getBoundingClientRect().top + scrollY + pads()[0] - top;
      /* Where the page is in the stretches: which filter, and how far through its run (0 to 1). */
      const where = (y) => {
        const offset = y - start();
        let index = stretches.findIndex((s) => offset < s.start + s.length);
        if (index < 0) index = count - 1;
        const s = stretches[Math.max(0, index)];
        const within = s.run ? Math.max(0, Math.min(1, (offset - s.start - s.hold) / s.run)) : 0;
        return { index: Math.max(0, index), within, s };
      };
      /* The page position for a filter and how far through its run. */
      const position = (index, within) => {
        const s = stretches[index];
        return start() + s.start + s.hold + within * s.run + (within >= 1 ? 1 : 0);
      };
      const current = () => this.filters.findIndex((link) => link.hasAttribute('aria-current'));
      const follow = () => {
        if (!this.pinned || !stretches.length) return;
        const { index, within } = where(scrollY);
        if (index !== current()) {
          this.show(index);
          const track = this.track;
          /* Arriving from either side, the products start where the scroll has them, not at the start and gliding. */
          if (sideways) {
            track.style.scrollSnapType = 'none';
            cancelAnimationFrame(frame);
            frame = 0;
            goal = within * (track.scrollWidth - track.clientWidth);
            track.scrollLeft = goal;
          }
          track.setAttribute('data-entering', arrival);
          arrival = 'scroll';
          track.addEventListener('animationend', () => track.removeAttribute('data-entering'), {
            once: true,
          });
          this.update();
          return;
        }
        if (!sideways) return;
        goal = within * (this.track.scrollWidth - this.track.clientWidth);
        if (!frame) frame = requestAnimationFrame(glide);
      };
      /* Stopped part way through a run: settle so a whole product rests at the row's edge (never while scrolling). */
      const settle = () => {
        if (!this.pinned || !sideways) return;
        const { index, within, s } = where(scrollY);
        if (!s.run || within <= 0 || within >= 1) return;
        const end = this.track.scrollWidth - this.track.clientWidth;
        const stops = this.stops();
        const nearest = stops.reduce((a, b) =>
          Math.abs(b - within * end) < Math.abs(a - within * end) ? b : a,
        );
        const y = position(index, nearest / end);
        if (Math.abs(y - scrollY) < 4) return;
        scrollTo({ top: y, behavior: 'smooth' });
      };
      let idle = 0;
      const scrolled = () => {
        follow();
        clearTimeout(idle);
        idle = setTimeout(settle, 180);
      };
      /* A filter chosen while held: straight to its stretch (not a smooth scroll through the ones between). */
      this.goTo = (index) => {
        arrival = 'chosen';
        scrollTo({ top: position(index, 0), behavior: 'instant' });
      };
      /* The arrows while held go the way the scroll does: through this filter's products, then on to the next filter
         (or back to the previous one's end). */
      /* Quick presses count on from the product last asked for, not from wherever the scroll has got to. */
      let aim = null;
      this.stepHeld = (direction) => {
        const index = current();
        const track = this.track;
        const end = track.scrollWidth - track.clientWidth;
        const recent = aim && aim.index === index && performance.now() - aim.time < 900;
        const at = recent ? aim.at : sideways ? goal : track.scrollLeft;
        const stops = this.stops();
        const next =
          direction > 0
            ? stops.find((stop) => stop > at + 2)
            : [...stops].reverse().find((stop) => stop < at - 2);
        const smooth = reduceMotion.matches ? 'auto' : 'smooth';
        if (next !== undefined && end > 0) {
          aim = { index, at: next, time: performance.now() };
          if (sideways) scrollTo({ top: position(index, next / end), behavior: smooth });
          else this.glide(next);
          return;
        }
        const to = index + direction;
        if (to < 0 || to >= count) return;
        aim = {
          index: to,
          at: direction > 0 ? 0 : Number.MAX_SAFE_INTEGER,
          time: performance.now(),
        };
        if (sideways) scrollTo({ top: position(to, direction > 0 ? 0 : 1), behavior: smooth });
        else this.goTo(to);
      };
      this.heldEnds = () => ({ first: current() === 0, last: current() === count - 1 });
      /* The page position for a filter and how far through its run (0 to 1), for anything that wants to go there. */
      this.positionOf = position;
      addEventListener('scroll', scrolled, { passive: true });
      addEventListener('resize', layout);
      addEventListener('load', layout);
      layout();
    }
    /* The chosen filter's page colours (a coffee range's, or none) mark the row, which the page follows while it is on
       screen; and the collection link follows the filter: its destination and its words. */
    mark(chosen) {
      ['Scheme', 'Tone', 'Color'].forEach((key) => {
        const value = chosen.dataset[`row${key}`];
        if (value) this.dataset[`page${key}`] = value;
        else delete this.dataset[`page${key}`];
      });
      const shop = this.querySelector('[data-row-shop]');
      if (shop) {
        shop.href = chosen.href;
        shop.textContent = chosen.dataset.rowShopLabel;
      }
    }
    show(index) {
      this.stop();
      const chosen = this.filters[index];
      this.filters.forEach((link) => {
        if (link === chosen) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      this.tracks.forEach((track, i) => {
        track.hidden = i !== index;
      });
      this.track = this.tracks[index];
      this.track.scrollLeft = 0;
      this.mark(chosen);
      document.dispatchEvent(new Event('page-schemes:update'));
      /* And the line under the heading says what the chosen range is for: all the lines share one space, only this one shows. */
      this.querySelectorAll('[data-row-note-index]').forEach((note) => {
        const on = Number(note.dataset.rowNoteIndex) === index;
        note.toggleAttribute('data-active', on);
        note.setAttribute('aria-hidden', String(!on));
      });
      this.update();
    }
    /* Where each card comes to rest: its left edge against the track's scroll padding, within the scrollable range. */
    stops() {
      const track = this.track;
      /* The left padding mirrors the scroll padding and, unlike it, always computes to pixels. */
      const edge =
        track.getBoundingClientRect().left + (parseFloat(getComputedStyle(track).paddingLeft) || 0);
      const end = track.scrollWidth - track.clientWidth;
      return [...track.children].map((card) =>
        Math.min(end, Math.max(0, card.getBoundingClientRect().left - edge + track.scrollLeft)),
      );
    }
    nearest(position) {
      const stops = this.stops();
      let best = 0;
      stops.forEach((stop, i) => {
        if (Math.abs(stop - position) < Math.abs(stops[best] - position)) best = i;
      });
      return { stops, index: best };
    }
    step(direction) {
      if (this.pinned) {
        this.stepHeld(direction);
        return;
      }
      const { stops, index } = this.nearest(this.target ?? this.track.scrollLeft);
      const next = Math.min(stops.length - 1, Math.max(0, index + direction));
      this.glide(stops[next]);
    }
    glide(to) {
      this.stop();
      const track = this.track;
      const from = track.scrollLeft;
      if (reduceMotion.matches || Math.abs(to - from) < 1) {
        track.scrollLeft = to;
        return;
      }
      this.target = to;
      track.style.scrollSnapType = 'none';
      const began = performance.now();
      const frame = (now) => {
        const t = Math.min(1, (now - began) / 700);
        track.scrollLeft = from + (to - from) * spring(t);
        if (t < 1) this.frame = requestAnimationFrame(frame);
        else this.stop(to);
      };
      this.frame = requestAnimationFrame(frame);
    }
    stop(settle) {
      cancelAnimationFrame(this.frame);
      if (settle !== undefined) this.track.scrollLeft = settle;
      if (this.target !== undefined && !this.dragging) this.track.style.scrollSnapType = '';
      this.target = undefined;
    }
    grab(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      const track = this.track;
      if (track.scrollWidth <= track.clientWidth) return;
      this.stop();
      const startX = event.clientX;
      const startScroll = track.scrollLeft;
      let lastX = startX;
      let lastTime = event.timeStamp;
      let velocity = 0;
      const move = (e) => {
        const dx = e.clientX - startX;
        if (!this.dragging && Math.abs(dx) < 6) return;
        if (!this.dragging) {
          this.dragging = true;
          this.dragged = true;
          track.style.scrollSnapType = 'none';
          track.setAttribute('data-dragging', '');
        }
        track.scrollLeft = startScroll - dx;
        const dt = Math.max(1, e.timeStamp - lastTime);
        velocity = 0.8 * ((lastX - e.clientX) / dt) + 0.2 * velocity;
        lastX = e.clientX;
        lastTime = e.timeStamp;
      };
      const release = () => {
        removeEventListener('pointermove', move);
        removeEventListener('pointerup', release);
        removeEventListener('pointercancel', release);
        if (!this.dragging) return;
        this.dragging = false;
        track.removeAttribute('data-dragging');
        /* Carry the throw a little, then settle on the nearest card with the spring. */
        const { stops, index } = this.nearest(track.scrollLeft + velocity * 220);
        this.target = stops[index];
        this.glide(stops[index]);
      };
      addEventListener('pointermove', move);
      addEventListener('pointerup', release);
      addEventListener('pointercancel', release);
    }
    update() {
      const end = this.track.scrollWidth - this.track.clientWidth - 1;
      /* Nothing to scroll: no chip, and nothing to drag. Held, the arrows also move between filters, so they stay. */
      this.buttons[0].parentElement.hidden = end <= 0 && !this.pinned;
      this.track.toggleAttribute('data-draggable', end > 0);
      const at = this.target ?? this.track.scrollLeft;
      const ends = this.pinned ? this.heldEnds() : { first: true, last: true };
      this.buttons.forEach((button) => {
        const back = Number(button.dataset.rowStep) < 0;
        button.disabled = back ? ends.first && at <= 0 : ends.last && at >= end;
      });
    }
  }
  if (!customElements.get('c4-product-row')) customElements.define('c4-product-row', C4ProductRow);
}
